import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';

// Google button is capped at 400x40px - so we scale it uniformly to fill wider containers without distortion
const GOOGLE_BUTTON_NATIVE_WIDTH = 400;
const GOOGLE_BUTTON_NATIVE_HEIGHT = 40;

const StyledGoogleButtonWrapper = styled.div<{ disabled?: boolean; height: number }>(({ disabled, height }) => ({
  width: '100%',
  height,
  ...(disabled && {
    pointerEvents: 'none',
    opacity: 0.6,
  }),
}));

const StyledGoogleButtonScaler = styled.div<{ scale: number }>(({ scale }) => ({
  width: GOOGLE_BUTTON_NATIVE_WIDTH,
  height: GOOGLE_BUTTON_NATIVE_HEIGHT,
  transform: `scale(${scale})`,
  transformOrigin: 'top left',
}));

export interface GoogleButtonProps {
  buttonRef: (node: HTMLDivElement | null) => void;
  className?: string;
  id?: string;
  disabled?: boolean;
}

export const GoogleButton: React.FC<GoogleButtonProps> = ({
  buttonRef,
  className,
  id,
  disabled = false,
}) => {
  const [scale, setScale] = useState(1);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const measure = useCallback(() => {
    const width = wrapperRef.current?.getBoundingClientRect().width;
    if (width) {
      setScale(width / GOOGLE_BUTTON_NATIVE_WIDTH);
    }
  }, []);

  const setWrapperRef = useCallback(
    (node: HTMLDivElement | null) => {
      wrapperRef.current = node;
      if (node) measure();
    },
    [measure]
  );

  useLayoutEffect(() => {
    if (!wrapperRef.current) return;
    // delay recalculation to next frame to avoid a resizing loop
    let frame: number | undefined;
    const resizeObserver = new ResizeObserver(() => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    resizeObserver.observe(wrapperRef.current);
    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      resizeObserver.disconnect();
    };
  }, [measure]);

  return (
    <StyledGoogleButtonWrapper
      ref={setWrapperRef}
      className={className}
      id={id}
      disabled={disabled}
      height={Math.ceil(GOOGLE_BUTTON_NATIVE_HEIGHT * scale)}
    >
      <StyledGoogleButtonScaler scale={scale}>
        <div ref={buttonRef} />
      </StyledGoogleButtonScaler>
    </StyledGoogleButtonWrapper>
  );
};
